// Last-known trip status per device (item 2 of the 2026-07-25 sync plan).
//
// The Trip Workspace picker and the dashboard's resume card list trips from
// DEVICE-LOCAL draft envelopes, which go stale the moment a slip is submitted
// somewhere else: Jill's Mac listed VVE26-VOY007 as an editable "Slip draft"
// days after the iPad filed it. The pickers need the trip's real status, and
// they need it OFFLINE too — so one batched, deadlined read against
// pilot_trips_v2 refreshes this cache whenever a picker is online, and every
// consumer reads the cache synchronously. An offline device shows the last
// CONFIRMED state instead of guessing, and a never-confirmed trip is labelled
// unconfirmed rather than defaulting to "Slip draft" (never present a blind
// check as truth — same rule as the workspace's loud account check).
import { supabase } from "../supabaseClient";
import { withTimeout } from "./submitQueue";

const KEY = "tripStatusCache_v1";
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// { [tripId]: { status, submitted_at, checked_at } } — always safe to call.
export function readTripStatusCache() {
  try { return JSON.parse(localStorage.getItem(KEY) || "{}") || {}; } catch { return {}; }
}

// One batched, deadlined refresh for the trips a picker has on screen.
// Returns the merged cache map. Throws when the read fails (offline, RLS-blind
// session) so callers can decide how loud to be — the stale cache from
// readTripStatusCache() remains valid last-known state either way.
export async function refreshTripStatusCache(tripIds) {
  const ids = [...new Set((tripIds || []).filter((id) => UUID_RE.test(String(id))))];
  if (!ids.length) return readTripStatusCache();
  const { data, error } = await withTimeout(
    supabase.from("pilot_trips_v2").select("id, status, submitted_at").in("id", ids),
    6000
  );
  if (error) throw error;
  const cache = readTripStatusCache();
  const now = Date.now();
  for (const row of data || []) {
    cache[String(row.id)] = {
      status: row.status || "draft",
      // pilot_trips_v2.submitted_at has a column DEFAULT of now(), so a DRAFT
      // row carries a meaningless creation-time stamp — storing it would let
      // the "Filed <date>" chip show a date up to weeks off (review 2026-07-25).
      // Only a non-draft status makes the stamp mean anything.
      submitted_at: (row.status && row.status !== "draft") ? row.submitted_at : null,
      checked_at: now,
    };
  }
  // A listed trip the DB doesn't know is still an answer ("no such trip") —
  // record the check so it isn't presented as never-verified forever.
  for (const id of ids) {
    if (!(data || []).some((r) => String(r.id) === id)) {
      cache[id] = { status: "unknown", submitted_at: null, checked_at: now };
    }
  }
  // The map is device-global and merge-only; cap it so years of trips cannot
  // grow it without bound. 300 entries is ~10x a busy season's trips.
  const keys = Object.keys(cache);
  if (keys.length > 300) {
    keys.sort((a, b) => (cache[b]?.checked_at || 0) - (cache[a]?.checked_at || 0));
    for (const k of keys.slice(300)) delete cache[k];
  }
  try { localStorage.setItem(KEY, JSON.stringify(cache)); } catch { /* quota — cache is a convenience */ }
  return cache;
}

// The one resolution rule, shared by every picker so they can never disagree:
// the device's own envelope saying "submitted" is truth (the submitting device
// knows), else the last confirmed DB status, else unknown.
export function resolveTripState(tripId, localEnvelopeStatus, cache) {
  if (localEnvelopeStatus === "submitted") return { state: "filed", confirmed: true, submitted_at: (cache || {})[tripId]?.submitted_at || null };
  const c = (cache || {})[String(tripId)];
  if (!c) return { state: "draft", confirmed: false, submitted_at: null };
  // The DB was asked and has never heard of this trip: a device-only id
  // (convert-to-editable copies mint these). Distinct from never-checked so
  // the label can say so instead of an eternal "(unconfirmed)".
  if (c.status === "unknown") return { state: "draft", confirmed: false, deviceOnly: true, submitted_at: null };
  // Any confirmed status OTHER than a live draft means the trip has left the
  // draft stage: submitted, approved, archived — and whatever gets added
  // later. Fail toward "filed" (review 2026-07-25): mislabelling a filed slip
  // as an editable draft re-arms its Delete button against the preserved
  // envelope and invites a stale resubmit over the office's record; the worst
  // a wrong "Filed" does is send the pilot to the Filed page to look.
  if (c.status !== "draft") return { state: "filed", confirmed: true, submitted_at: c.submitted_at };
  return { state: "draft", confirmed: true, submitted_at: null };
}
